// VariantOptionsBuilder component for creating and managing product variant options
// Supports dynamic option creation, intelligent suggestions, and variant combination generation

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { 
  MerchantProductSelectionModal, 
  MerchantProductBrowserModal,
  type MerchantProduct 
} from 'src/shared/components'
import { variantIntegrationService } from 'src/app/modules/merchant-product/services/variantIntegrationService'
import { skuGenerationService } from 'src/app/modules/merchant-product/services/skuGenerationService'

// Enum for option types
export enum VariantOptionType {
  COLOR = 'color',
  SIZE = 'size', 
  MATERIAL = 'material',
  STYLE = 'style',
  CUSTOM = 'custom'
}

// Status enum for variants
export enum VariantStatus {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
  OUT_OF_STOCK = 'out_of_stock'
}

// Interface for individual option values
export interface VariantOptionValue {
  id: string
  value: string
  displayName: string
  colorCode?: string // For color options
  imageUrl?: string // For visual options
  metadata?: Record<string, any>
}

// Interface for variant options
export interface VariantOption {
  id: string
  name: string
  type: VariantOptionType
  values: VariantOptionValue[]
  required: boolean
  sortOrder: number
}

// Interface for generated variant combinations
export interface VariantCombination {
  id: string
  sku: string
  options: Record<string, string> // optionId -> valueId mapping
  optionValues: Record<string, VariantOptionValue> // optionId -> value object
  price?: number
  compareAtPrice?: number
  stock: number
  warehouseStock?: Record<string, number> // Warehouse-specific stock breakdown
  status: VariantStatus
  weight?: number
  dimensions?: {
    length?: number
    width?: number
    height?: number
  }
  images?: string[]
  imageUrl?: string
  barcode?: string
  metadata?: Record<string, any>
  linkedMerchantProduct?: any // MerchantProduct object when linked
}

// Product context for intelligent suggestions
export interface ProductContext {
  id?: string
  name?: string
  category?: string
  subcategory?: string
  brand?: string
  description?: string
  tags?: string[]
  existingVariants?: VariantCombination[]
  linkedMerchantProduct?: any // MerchantProduct object when linked
}

// SKU generation pattern configuration
export interface SKUPattern {
  prefix?: string
  suffix?: string
  separator?: string
  includeProductId?: boolean
  customPattern?: string // Template like "{prefix}-{option1}-{option2}-{suffix}"
}

// Component props interface
export interface VariantOptionsBuilderProps {
  /** Current variant options */
  options?: VariantOption[]
  
  /** Generated variant combinations */
  variants?: VariantCombination[]
  
  /** Callback when options change */
  onOptionsChange?: (options: VariantOption[]) => void
  
  /** Callback when variants change */
  onVariantsChange?: (variants: VariantCombination[]) => void
  
  /** Product context for intelligent suggestions */
  productContext?: ProductContext
  
  /** Maximum number of options allowed */
  maxOptions?: number
  
  /** Maximum number of variants allowed */
  maxVariants?: number
  
  /** SKU generation pattern */
  skuPattern?: SKUPattern
  
  /** Default base price for variants */
  basePrice?: number
  
  /** Default stock quantity for variants */
  defaultStock?: number
  
  /** Whether to auto-generate variants on option changes */
  autoGenerate?: boolean
  
  /** Custom CSS class name */
  className?: string
  
  /** Whether the component is disabled */
  disabled?: boolean
  
  /** Loading state */
  loading?: boolean
  
  /** Error message to display */
  error?: string | null
  
  /** Whether to show bulk operations panel */
  showBulkOperations?: boolean
}

// Hook for managing variant options state
export interface UseVariantOptionsState {
  options: VariantOption[]
  variants: VariantCombination[]
  loading: boolean
  error: string | null
  
  // Option management
  addOption: (option: Omit<VariantOption, 'id' | 'sortOrder'>) => void
  updateOption: (id: string, updates: Partial<VariantOption>) => void
  removeOption: (id: string) => void
  reorderOptions: (sourceIndex: number, destinationIndex: number) => void
  
  // Value management
  addValue: (optionId: string, value: Omit<VariantOptionValue, 'id'>) => void
  updateValue: (optionId: string, valueId: string, updates: Partial<VariantOptionValue>) => void
  removeValue: (optionId: string, valueId: string) => void
  
  // Variant management
  generateVariants: () => void
  updateVariant: (id: string, updates: Partial<VariantCombination>) => void
  bulkUpdateVariants: (variantIds: string[], updates: Partial<VariantCombination>) => void
  deleteVariant: (id: string) => void
  
  // Utility functions
  getVariantCount: () => number
  validateOptions: () => string[]
  exportData: () => string
  importData: (data: string) => boolean
}




// Utility function for generating unique IDs
const generateId = (prefix: string = 'item'): string => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Utility function for generating SKUs
const generateSKU = (
  combination: Record<string, VariantOptionValue>,
  pattern: SKUPattern,
  productContext?: ProductContext
): string => {
  const {
    prefix = '',
    suffix = '',
    separator = '-',
    includeProductId = false,
    customPattern
  } = pattern

  let sku = ''
  
  if (customPattern) {
    // Use template pattern like "{prefix}-{option1}-{option2}-{suffix}"
    sku = customPattern
    
    // Replace placeholders
    sku = sku.replace('{prefix}', prefix)
    sku = sku.replace('{suffix}', suffix)
    sku = sku.replace('{product}', productContext?.name?.slice(0, 3).toUpperCase() || 'PRD')
    
    // Replace option values
    Object.entries(combination).forEach(([optionId, value]) => {
      const placeholder = `{${optionId.toLowerCase()}}`
      const replacement = value.value.slice(0, 3).toUpperCase()
      sku = sku.replace(placeholder, replacement)
    })
    
    // Clean up any remaining placeholders
    sku = sku.replace(/\{[^}]+\}/g, '')
  } else {
    // Build SKU from components
    const parts: string[] = []
    
    if (prefix) parts.push(prefix)
    if (includeProductId && productContext?.id) {
      parts.push(productContext.id.slice(0, 6).toUpperCase())
    }
    
    // Add option values in sorted order
    Object.values(combination)
      .sort((a, b) => a.value.localeCompare(b.value))
      .forEach(value => {
        parts.push(value.value.slice(0, 3).toUpperCase())
      })
    
    if (suffix) parts.push(suffix)
    
    sku = parts.filter(Boolean).join(separator)
  }
  
  return sku.toUpperCase().replace(/[^A-Z0-9-_]/g, '')
}

// Enhanced Bulk Operations for Variant Management
const VariantBulkOperations = {
  // Pricing operations
  updatePrices: (variants: VariantCombination[], operation: 'set' | 'increase' | 'decrease', value: number): VariantCombination[] => {
    return variants.map(variant => ({
      ...variant,
      price: operation === 'set' ? value : 
             operation === 'increase' ? (variant.price || 0) + value :
             Math.max(0, (variant.price || 0) - value)
    }))
  },

  // Stock operations
  updateStock: (variants: VariantCombination[], operation: 'set' | 'increase' | 'decrease', value: number): VariantCombination[] => {
    return variants.map(variant => ({
      ...variant,
      stock: operation === 'set' ? value : 
             operation === 'increase' ? variant.stock + value :
             Math.max(0, variant.stock - value)
    }))
  },

  // Compare at price operations
  updateCompareAtPrices: (variants: VariantCombination[], operation: 'set' | 'clear' | 'percentage', value?: number): VariantCombination[] => {
    return variants.map(variant => ({
      ...variant,
      compareAtPrice: operation === 'clear' ? undefined :
                      operation === 'set' ? value :
                      operation === 'percentage' && value ? (variant.price || 0) * (1 + value / 100) :
                      variant.compareAtPrice
    }))
  },

  // Status operations
  updateStatus: (variants: VariantCombination[], status: VariantStatus): VariantCombination[] => {
    return variants.map(variant => ({
      ...variant,
      status
    }))
  },

  // Filter operations
  filterByOption: (variants: VariantCombination[], optionId: string, valueIds: string[]): VariantCombination[] => {
    return variants.filter(variant => 
      valueIds.includes(variant.options[optionId])
    )
  },

  filterByStatus: (variants: VariantCombination[], statuses: VariantStatus[]): VariantCombination[] => {
    return variants.filter(variant => statuses.includes(variant.status))
  },

  filterByPriceRange: (variants: VariantCombination[], min: number, max: number): VariantCombination[] => {
    return variants.filter(variant => {
      const price = variant.price || 0
      return price >= min && price <= max
    })
  },

  filterByStockRange: (variants: VariantCombination[], min: number, max: number): VariantCombination[] => {
    return variants.filter(variant => 
      variant.stock >= min && variant.stock <= max
    )
  },

  // SKU operations
  regenerateSKUs: (variants: VariantCombination[], pattern: SKUPattern, productContext?: ProductContext): VariantCombination[] => {
    return variants.map(variant => ({
      ...variant,
      sku: generateSKU(variant.optionValues, pattern, productContext)
    }))
  },

  // Duplicate operations
  findDuplicateSKUs: (variants: VariantCombination[]): Record<string, VariantCombination[]> => {
    const skuGroups: Record<string, VariantCombination[]> = {}
    
    variants.forEach(variant => {
      if (!skuGroups[variant.sku]) {
        skuGroups[variant.sku] = []
      }
      skuGroups[variant.sku].push(variant)
    })
    
    // Return only groups with duplicates
    return Object.fromEntries(
      Object.entries(skuGroups).filter(([, group]) => group.length > 1)
    )
  },

  // Sorting operations
  sortByPrice: (variants: VariantCombination[], direction: 'asc' | 'desc' = 'asc'): VariantCombination[] => {
    return [...variants].sort((a, b) => {
      const priceA = a.price || 0
      const priceB = b.price || 0
      return direction === 'asc' ? priceA - priceB : priceB - priceA
    })
  },

  sortByStock: (variants: VariantCombination[], direction: 'asc' | 'desc' = 'asc'): VariantCombination[] => {
    return [...variants].sort((a, b) => {
      return direction === 'asc' ? a.stock - b.stock : b.stock - a.stock
    })
  },

  sortBySKU: (variants: VariantCombination[], direction: 'asc' | 'desc' = 'asc'): VariantCombination[] => {
    return [...variants].sort((a, b) => {
      return direction === 'asc' ? a.sku.localeCompare(b.sku) : b.sku.localeCompare(a.sku)
    })
  },

  // Validation operations
  validateVariants: (variants: VariantCombination[]): { valid: VariantCombination[], invalid: Array<VariantCombination & { errors: string[] }> } => {
    const valid: VariantCombination[] = []
    const invalid: Array<VariantCombination & { errors: string[] }> = []

    variants.forEach(variant => {
      const errors: string[] = []

      // Check required fields
      if (!variant.sku || variant.sku.trim() === '') {
        errors.push('SKU is required')
      }

      if (variant.price === undefined || variant.price < 0) {
        errors.push('Price must be a positive number')
      }

      if (variant.stock < 0) {
        errors.push('Stock cannot be negative')
      }

      // Check SKU format (basic validation)
      if (variant.sku && !/^[A-Z0-9\-_\/]+$/.test(variant.sku)) {
        errors.push('SKU contains invalid characters')
      }

      if (errors.length > 0) {
        invalid.push({ ...variant, errors })
      } else {
        valid.push(variant)
      }
    })

    return { valid, invalid }
  },

  // Export/Import operations
  exportToCSV: (variants: VariantCombination[]): string => {
    const headers = ['SKU', 'Price', 'Compare At Price', 'Stock', 'Status', 'Options']
    const rows = variants.map(variant => [
      variant.sku,
      variant.price?.toString() || '',
      variant.compareAtPrice?.toString() || '',
      variant.stock.toString(),
      variant.status,
      Object.entries(variant.optionValues)
        .map(([optionId, value]) => `${optionId}:${value.value}`)
        .join(';')
    ])
    
    return [headers, ...rows].map(row => row.join(',')).join('\n')
  },

  // Statistical operations
  getStats: (variants: VariantCombination[]) => {
    if (variants.length === 0) {
      return {
        total: 0,
        totalPrice: 0,
        averagePrice: 0,
        totalStock: 0,
        averageStock: 0,
        statusCounts: {},
        priceRange: { min: 0, max: 0 },
        stockRange: { min: 0, max: 0 }
      }
    }

    const prices = variants.map(v => v.price || 0)
    const stocks = variants.map(v => v.stock)
    const statusCounts = variants.reduce((acc, variant) => {
      acc[variant.status] = (acc[variant.status] || 0) + 1
      return acc
    }, {} as Record<VariantStatus, number>)

    return {
      total: variants.length,
      totalPrice: prices.reduce((sum, price) => sum + price, 0),
      averagePrice: prices.reduce((sum, price) => sum + price, 0) / variants.length,
      totalStock: stocks.reduce((sum, stock) => sum + stock, 0),
      averageStock: stocks.reduce((sum, stock) => sum + stock, 0) / variants.length,
      statusCounts,
      priceRange: {
        min: Math.min(...prices),
        max: Math.max(...prices)
      },
      stockRange: {
        min: Math.min(...stocks),
        max: Math.max(...stocks)
      }
    }
  }
}

// Main VariantOptionsBuilder component
const VariantOptionsBuilder: React.FC<VariantOptionsBuilderProps> = ({
  options: initialOptions = [],
  variants: initialVariants = [],
  onOptionsChange,
  onVariantsChange,
  productContext,
  maxOptions = 10,
  maxVariants = 100,
  skuPattern = { separator: '-' },
  basePrice = 0,
  defaultStock = 0,
  autoGenerate = true,
  className = '',
  disabled = false,
  loading = false,
  error = null,
  showBulkOperations = false
}) => {
  // Component state
  const [options, setOptions] = useState<VariantOption[]>(initialOptions)
  const [variants, setVariants] = useState<VariantCombination[]>(initialVariants)
  const [isGenerating, setIsGenerating] = useState(false)
  const [draggedOptionIndex, setDraggedOptionIndex] = useState<number | null>(null)

  // Refs to store latest callback references to avoid infinite loops
  const onOptionsChangeRef = useRef(onOptionsChange)
  const onVariantsChangeRef = useRef(onVariantsChange)
  
  // Update refs when props change
  useEffect(() => {
    onOptionsChangeRef.current = onOptionsChange
  }, [onOptionsChange])
  
  useEffect(() => {
    onVariantsChangeRef.current = onVariantsChange
  }, [onVariantsChange])

  // Generate all possible variant combinations
  const generateVariantCombinations = useCallback(() => {
    if (options.length === 0) {
      return []
    }

    // Get all options with values
    const optionsWithValues = options.filter(option => option.values.length > 0)
    
    if (optionsWithValues.length === 0) {
      return []
    }

    // Generate cartesian product of all option values
    const cartesianProduct = (arrays: VariantOptionValue[][]): VariantOptionValue[][] => {
      return arrays.reduce((acc, curr) => {
        return acc.flatMap(accItem => 
          curr.map(currItem => [...accItem, currItem])
        )
      }, [[]] as VariantOptionValue[][])
    }

    const valueArrays = optionsWithValues.map(option => option.values)
    const combinations = cartesianProduct(valueArrays)

    // Convert combinations to variant objects
    const generatedVariants: VariantCombination[] = combinations.map(combination => {
      const optionMap: Record<string, string> = {}
      const valueMap: Record<string, VariantOptionValue> = {}
      
      combination.forEach((value, index) => {
        const option = optionsWithValues[index]
        optionMap[option.id] = value.id
        valueMap[option.id] = value
      })

      const sku = generateSKU(valueMap, skuPattern, productContext)
      
      return {
        id: generateId('variant'),
        sku,
        options: optionMap,
        optionValues: valueMap,
        price: basePrice,
        stock: defaultStock,
        status: VariantStatus.ENABLED
      }
    })

    return generatedVariants.slice(0, maxVariants) // Limit to maxVariants
  }, [options, skuPattern, productContext, basePrice, defaultStock, maxVariants])

  // Create stable reference for options to prevent infinite loops
  const optionsString = useMemo(() => {
    return JSON.stringify(options.map(opt => ({
      id: opt.id,
      name: opt.name,
      type: opt.type,
      values: opt.values.map(v => ({ id: v.id, value: v.value }))
    })))
  }, [options])

  // Auto-generate variants when options change - completely self-contained
  useEffect(() => {
    if (!autoGenerate) return
    
    if (options.length === 0) {
      setVariants([])
      return
    }
    
    setIsGenerating(true)
    
    // Get all options with values
    const optionsWithValues = options.filter(option => option.values.length > 0)
    
    if (optionsWithValues.length === 0) {
      setVariants([])
      setIsGenerating(false)
      return
    }

    // Generate cartesian product - self-contained to avoid dependency issues
    const cartesianProduct = (arrays: VariantOptionValue[][]): VariantOptionValue[][] => {
      return arrays.reduce((acc, curr) => {
        return acc.flatMap(accItem => 
          curr.map(currItem => [...accItem, currItem])
        )
      }, [[]] as VariantOptionValue[][])
    }

    const valueArrays = optionsWithValues.map(option => option.values)
    const combinations = cartesianProduct(valueArrays)

    // Convert combinations to variant objects
    const generatedVariants: VariantCombination[] = combinations.map(combination => {
      const optionMap: Record<string, string> = {}
      const valueMap: Record<string, VariantOptionValue> = {}
      
      combination.forEach((value, index) => {
        const option = optionsWithValues[index]
        optionMap[option.id] = value.id
        valueMap[option.id] = value
      })

      const sku = generateSKU(valueMap, skuPattern, productContext)
      
      return {
        id: generateId('variant'),
        sku,
        options: optionMap,
        optionValues: valueMap,
        price: basePrice,
        stock: defaultStock,
        status: VariantStatus.ENABLED
      }
    })

    const newVariants = generatedVariants.slice(0, maxVariants)
    setVariants(newVariants)
    onVariantsChangeRef.current?.(newVariants)
    setIsGenerating(false)
  }, [autoGenerate, optionsString])

  // Update parent when options change - with stable dependency
  useEffect(() => {
    onOptionsChangeRef.current?.(options)
  }, [optionsString])

  // Calculate total possible combinations
  const totalPossibleCombinations = useMemo(() => {
    return options.reduce((total, option) => {
      return option.values.length > 0 ? total * option.values.length : total
    }, 1)
  }, [options])

  // Check if max variants would be exceeded
  const wouldExceedMaxVariants = totalPossibleCombinations > maxVariants

  // Option management functions
  const addOption = useCallback((name: string, type: VariantOptionType) => {
    if (options.length >= maxOptions) {
      return false
    }

    // Check for duplicate names
    const nameExists = options.some(option => 
      option.name.toLowerCase() === name.toLowerCase()
    )

    if (nameExists) {
      return false
    }

    const newOption: VariantOption = {
      id: generateId('option'),
      name: name.trim(),
      type,
      values: [],
      required: false,
      sortOrder: options.length
    }

    const updatedOptions = [...options, newOption]
    setOptions(updatedOptions)
    return true
  }, [options, maxOptions])

  const removeOption = useCallback((optionId: string) => {
    const updatedOptions = options.filter(option => option.id !== optionId)
    // Update sort orders
    const reorderedOptions = updatedOptions.map((option, index) => ({
      ...option,
      sortOrder: index
    }))
    setOptions(reorderedOptions)
  }, [options])

  const updateOption = useCallback((optionId: string, updates: Partial<VariantOption>) => {
    const updatedOptions = options.map(option => 
      option.id === optionId ? { ...option, ...updates } : option
    )
    setOptions(updatedOptions)
  }, [options])

  const reorderOptions = useCallback((sourceIndex: number, destinationIndex: number) => {
    const newOptions = Array.from(options)
    const [removed] = newOptions.splice(sourceIndex, 1)
    newOptions.splice(destinationIndex, 0, removed)
    
    // Update sort orders
    const reorderedOptions = newOptions.map((option, index) => ({
      ...option,
      sortOrder: index
    }))
    
    setOptions(reorderedOptions)
  }, [options])

  // Value management functions
  const addValue = useCallback((optionId: string, value: string, displayName?: string) => {
    if (!value.trim()) return false

    const option = options.find(opt => opt.id === optionId)
    if (!option) return false

    // Check for duplicate values
    const valueExists = option.values.some(v => 
      v.value.toLowerCase() === value.toLowerCase()
    )

    if (valueExists) return false

    const newValue: VariantOptionValue = {
      id: generateId('value'),
      value: value.trim(),
      displayName: displayName?.trim() || value.trim(),
      // No additional properties needed
    }

    const updatedOptions = options.map(opt => 
      opt.id === optionId 
        ? { ...opt, values: [...opt.values, newValue] }
        : opt
    )

    setOptions(updatedOptions)
    return true
  }, [options])

  const removeValue = useCallback((optionId: string, valueId: string) => {
    const updatedOptions = options.map(opt => 
      opt.id === optionId 
        ? { ...opt, values: opt.values.filter(v => v.id !== valueId) }
        : opt
    )
    setOptions(updatedOptions)
  }, [options])

  const updateValue = useCallback((optionId: string, valueId: string, updates: Partial<VariantOptionValue>) => {
    const updatedOptions = options.map(opt => 
      opt.id === optionId 
        ? {
            ...opt,
            values: opt.values.map(v => 
              v.id === valueId ? { ...v, ...updates } : v
            )
          }
        : opt
    )
    setOptions(updatedOptions)
  }, [options])


  // Drag and drop handlers (simplified without react-beautiful-dnd for now)
  const handleDragStart = useCallback((index: number) => {
    setDraggedOptionIndex(index)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    if (draggedOptionIndex !== null && draggedOptionIndex !== dropIndex) {
      reorderOptions(draggedOptionIndex, dropIndex)
    }
    setDraggedOptionIndex(null)
  }, [draggedOptionIndex, reorderOptions])

  // Bulk Operations Functions - Enhanced for Task 25.4
  const bulkUpdatePrices = useCallback((operation: 'set' | 'increase' | 'decrease', value: number) => {
    const updatedVariants = VariantBulkOperations.updatePrices(variants, operation, value)
    setVariants(updatedVariants)
    onVariantsChangeRef.current?.(updatedVariants)
  }, [variants])

  const bulkUpdateStock = useCallback((operation: 'set' | 'increase' | 'decrease', value: number) => {
    const updatedVariants = VariantBulkOperations.updateStock(variants, operation, value)
    setVariants(updatedVariants)
    onVariantsChangeRef.current?.(updatedVariants)
  }, [variants])

  const bulkUpdateStatus = useCallback((status: VariantStatus) => {
    const updatedVariants = VariantBulkOperations.updateStatus(variants, status)
    setVariants(updatedVariants)
    onVariantsChangeRef.current?.(updatedVariants)
  }, [variants])

  const regenerateAllSKUs = useCallback((newPattern?: SKUPattern) => {
    const pattern = newPattern || skuPattern
    const updatedVariants = VariantBulkOperations.regenerateSKUs(variants, pattern, productContext)
    setVariants(updatedVariants)
    onVariantsChangeRef.current?.(updatedVariants)
  }, [variants, skuPattern, productContext])

  const validateAllVariants = useCallback(() => {
    return VariantBulkOperations.validateVariants(variants)
  }, [variants])

  const exportVariantsToCSV = useCallback(() => {
    return VariantBulkOperations.exportToCSV(variants)
  }, [variants])

  const getVariantStats = useCallback(() => {
    return VariantBulkOperations.getStats(variants)
  }, [variants])

  const findDuplicateSKUs = useCallback(() => {
    return VariantBulkOperations.findDuplicateSKUs(variants)
  }, [variants])

  const sortVariants = useCallback((by: 'price' | 'stock' | 'sku', direction: 'asc' | 'desc' = 'asc') => {
    let sortedVariants: VariantCombination[]
    
    switch (by) {
      case 'price':
        sortedVariants = VariantBulkOperations.sortByPrice(variants, direction)
        break
      case 'stock':
        sortedVariants = VariantBulkOperations.sortByStock(variants, direction)
        break
      case 'sku':
        sortedVariants = VariantBulkOperations.sortBySKU(variants, direction)
        break
      default:
        return
    }
    
    setVariants(sortedVariants)
    onVariantsChangeRef.current?.(sortedVariants)
  }, [variants])

  // Variant Integration Service Methods
  const linkVariantsToMerchantProducts = useCallback((merchantProducts: MerchantProduct[], linkingStrategy: 'auto' | 'manual' = 'auto') => {
    if (!productContext?.id) return
    
    const result = variantIntegrationService.linkMerchantProductsToVariants(
      productContext.id,
      variants,
      merchantProducts,
      linkingStrategy
    )
    
    if (result.success) {
      // Update variants with linked products and generated SKUs
      const updatedVariants = variants.map(variant => {
        if (result.linkedVariants.includes(variant.id) && result.generatedSkus[variant.id]) {
          return {
            ...variant,
            sku: result.generatedSkus[variant.id]
          }
        }
        return variant
      })
      
      setVariants(updatedVariants)
      onVariantsChangeRef.current?.(updatedVariants)
    }
    
    return result
  }, [variants, productContext])
  
  const generateVariantSKUs = useCallback((skuOptions: any) => {
    if (!productContext?.id) return
    
    const generatedSKUs = variantIntegrationService.generateVariantSKUs(
      productContext.id,
      variants,
      skuOptions
    )
    
    // Update variants with new SKUs
    const updatedVariants = variants.map(variant => ({
      ...variant,
      sku: generatedSKUs[variant.id] || variant.sku
    }))
    
    setVariants(updatedVariants)
    onVariantsChangeRef.current?.(updatedVariants)
  }, [variants, productContext])
  
  const establishProductHierarchy = useCallback(() => {
    if (!productContext?.id) return null
    
    const linkedMerchantProducts = new Map<string, MerchantProduct>()
    variants.forEach(variant => {
      if (variant.linkedMerchantProduct) {
        linkedMerchantProducts.set(variant.id, variant.linkedMerchantProduct)
      }
    })
    
    return variantIntegrationService.establishProductHierarchy(
      productContext,
      variants,
      linkedMerchantProducts
    )
  }, [variants, productContext])

  // Advanced variant filtering
  const filterVariants = useCallback((filters: {
    optionFilters?: { optionId: string, valueIds: string[] }[]
    statusFilters?: VariantStatus[]
    priceRange?: { min: number, max: number }
    stockRange?: { min: number, max: number }
  }) => {
    let filteredVariants = [...variants]

    // Apply option filters
    if (filters.optionFilters) {
      filters.optionFilters.forEach(({ optionId, valueIds }) => {
        filteredVariants = VariantBulkOperations.filterByOption(filteredVariants, optionId, valueIds)
      })
    }

    // Apply status filters
    if (filters.statusFilters) {
      filteredVariants = VariantBulkOperations.filterByStatus(filteredVariants, filters.statusFilters)
    }

    // Apply price range filter
    if (filters.priceRange) {
      filteredVariants = VariantBulkOperations.filterByPriceRange(
        filteredVariants, 
        filters.priceRange.min, 
        filters.priceRange.max
      )
    }

    // Apply stock range filter
    if (filters.stockRange) {
      filteredVariants = VariantBulkOperations.filterByStockRange(
        filteredVariants,
        filters.stockRange.min,
        filters.stockRange.max
      )
    }

    return filteredVariants
  }, [variants])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'n':
            e.preventDefault()
            // Focus on the new option input instead of showing modal
            const optionInput = document.querySelector('[data-option-input]') as HTMLInputElement
            optionInput?.focus()
            break
        }
      }
    }

    if (!disabled) {
      document.addEventListener('keydown', handleKeydown)
      return () => document.removeEventListener('keydown', handleKeydown)
    }
  }, [disabled])


  return (
    <div className={`tw-w-full tw-space-y-6 ${className}`}>
      {/* Component header matching reference design */}
      <div className="tw-mb-6 tw-flex tw-justify-between tw-items-start">
        <div>
          <h4 className="tw-text-base tw-font-bold tw-text-gray-900 dark:tw-text-white">Variant Options</h4>
          <p className="tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">Add different options like size, material, style, etc.</p>
        </div>
        <button 
          onClick={() => {
            if (options.length < maxOptions) {
              const newOption: VariantOption = {
                id: generateId('option'),
                name: '',
                type: VariantOptionType.CUSTOM,
                values: [],
                required: false,
                sortOrder: options.length
              }
              setOptions([...options, newOption])
            }
          }}
          disabled={disabled || options.length >= maxOptions}
          className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-blue-600 hover:tw-text-blue-500 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
        >
          <span className="material-symbols-outlined tw-text-[20px]">add_circle</span>
          Add Option
        </button>
      </div>

      {/* Error display */}
      {error && (
        <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md tw-p-4">
          <div className="tw-flex">
            <span className="material-symbols-outlined tw-text-red-400 tw-mr-2">
              error
            </span>
            <div className="tw-text-red-800 tw-text-sm">{error}</div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {(loading || isGenerating) && (
        <div className="tw-flex tw-items-center tw-justify-center tw-py-8">
          <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
          <span className="tw-ml-3 tw-text-gray-600">
            {isGenerating ? 'Generating variants...' : 'Loading...'}
          </span>
        </div>
      )}


      {/* Variant Options Content */}
      <div className="tw-flex tw-flex-col tw-gap-6 tw-mb-6">

        {/* Options list */}
        {options.length === 0 ? (
          <div className="tw-text-center tw-py-8 tw-text-gray-500">
            <span className="material-symbols-outlined tw-text-4xl tw-text-gray-300 tw-mb-2 tw-block">
              tune
            </span>
            <p className="tw-text-sm">No options added yet.</p>
            <p className="tw-text-xs tw-text-gray-400 tw-mt-1">
              Add options like Size, Material, or Style to create variants.
            </p>
          </div>
        ) : (
          <div className="tw-space-y-6">
            {options
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((option, index) => (
                <OptionCard
                  key={option.id}
                  option={option}
                  index={index}
                  onUpdate={(updates) => updateOption(option.id, updates)}
                  onRemove={() => removeOption(option.id)}
                  onAddValue={(value, displayName) => addValue(option.id, value, displayName)}
                  onUpdateValue={(valueId, updates) => updateValue(option.id, valueId, updates)}
                  onRemoveValue={(valueId) => removeValue(option.id, valueId)}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  disabled={disabled}
                />
              ))}
          </div>
        )}
      </div>


      {/* Variants preview table section - Task 25.5 */}
      <VariantPreviewTable
        variants={variants}
        options={options}
        onVariantUpdate={(id, updates) => setVariants(prev => 
          prev.map(v => v.id === id ? { ...v, ...updates } : v)
        )}
        onVariantDelete={(id) => setVariants(prev => prev.filter(v => v.id !== id))}
        onBulkOperation={(operation, variantIds, value) => {
          switch (operation) {
            case 'updatePrices':
              bulkUpdatePrices('set', value)
              break
            case 'increasePrices':
              bulkUpdatePrices('increase', value)
              break
            case 'decreasePrices':
              bulkUpdatePrices('decrease', value)
              break
            case 'updateStock':
              bulkUpdateStock('set', value) 
              break
            case 'increaseStock':
              bulkUpdateStock('increase', value)
              break
            case 'decreaseStock':
              bulkUpdateStock('decrease', value)
              break
            case 'updateStatus':
              bulkUpdateStatus(value as VariantStatus)
              break
            case 'regenerateSKUs':
              regenerateAllSKUs()
              break
            case 'deleteSelected':
              setVariants(prev => prev.filter(v => !variantIds.includes(v.id)))
              break
            case 'linkAllVariants':
              // Open merchant product modal for bulk linking
              if (value && Array.isArray(value)) {
                linkVariantsToMerchantProducts(value, 'auto')
              }
              break
            case 'generateAllSKUs':
              generateVariantSKUs(value)
              break
            case 'unlinkAll':
              if (Array.isArray(value)) {
                setVariants(value)
                onVariantsChangeRef.current?.(value)
              }
              break
          }
        }}
        loading={loading}
        disabled={disabled}
        showBulkOperations={showBulkOperations}
        // Pass integration methods to VariantPreviewTable
        onLinkVariants={linkVariantsToMerchantProducts}
        onGenerateSKUs={generateVariantSKUs}
        onEstablishHierarchy={establishProductHierarchy}
      />
    </div>
  )
}

// Option Card Component
interface OptionCardProps {
  option: VariantOption
  index: number
  onUpdate: (updates: Partial<VariantOption>) => void
  onRemove: () => void
  onAddValue: (value: string, displayName?: string) => boolean
  onUpdateValue: (valueId: string, updates: Partial<VariantOptionValue>) => void
  onRemoveValue: (valueId: string) => void
  onDragStart: () => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
  disabled?: boolean
}

const OptionCard: React.FC<OptionCardProps> = ({
  option,
  index,
  onUpdate,
  onRemove,
  onAddValue,
  onUpdateValue,
  onRemoveValue,
  onDragStart,
  onDragOver,
  onDrop,
  disabled = false
}) => {
  const [newValue, setNewValue] = useState('')
  const [newDisplayName, setNewDisplayName] = useState('')
  const [showAddValue, setShowAddValue] = useState(false)
  const [editingValueId, setEditingValueId] = useState<string | null>(null)
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)
  const [showOptionDropdown, setShowOptionDropdown] = useState(false)

  const handleAddValue = () => {
    if (newValue.trim()) {
      const success = onAddValue(
        newValue,
        newDisplayName.trim() || undefined
      )
      if (success) {
        setNewValue('')
        setNewDisplayName('')
        // Clear form
        setShowAddValue(false)
      }
    }
  }

  const getOptionTypeIcon = (type: VariantOptionType) => {
    switch (type) {
      case VariantOptionType.COLOR:
        return 'tune'
      case VariantOptionType.SIZE:
        return 'straighten'
      case VariantOptionType.MATERIAL:
        return 'texture'
      case VariantOptionType.STYLE:
        return 'style'
      default:
        return 'tune'
    }
  }

  return (
    <div 
      className="tw-p-4 sm:tw-p-5 tw-rounded-lg tw-bg-gray-50 dark:tw-bg-[#151f28] tw-border tw-border-gray-200 dark:tw-border-[#364152]"
      draggable={!disabled}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* Header with drag handle, option type dropdown, and delete */}
      <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between sm:tw-items-center tw-gap-4 tw-mb-4">
        <div className="tw-flex tw-items-center tw-gap-3">
          <span className="material-symbols-outlined tw-text-gray-400 tw-text-[20px] tw-cursor-move">drag_indicator</span>
          <div className="tw-flex tw-flex-col tw-flex-1">
            <label className="tw-text-xs tw-font-medium tw-text-gray-600 dark:tw-text-gray-400 tw-mb-1">Option Type</label>
            <div className="tw-relative">
              <input 
                type="text"
                value={option.name}
                onChange={(e) => {
                  onUpdate({ name: e.target.value })
                  setShowOptionDropdown(true)
                }}
                onClick={() => setShowOptionDropdown(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setShowOptionDropdown(false)
                  }
                }}
                className="tw-w-full tw-bg-white dark:tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-cursor-pointer" 
                placeholder="Click to select or enter custom option..." 
                disabled={disabled}
              />
              <span className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 material-symbols-outlined tw-text-gray-400 tw-text-[16px] tw-pointer-events-none">expand_more</span>
              
              {/* Dropdown with common options */}
              {showOptionDropdown && (
                <div 
                  className="tw-absolute tw-top-full tw-left-0 tw-right-0 tw-mt-1 tw-bg-white dark:tw-bg-[#1C252E] tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-shadow-lg tw-z-20 tw-max-h-48 tw-overflow-y-auto"
                  onBlur={() => setShowOptionDropdown(false)}
                >
                  <div className="tw-p-2 tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-border-b tw-border-gray-200 dark:tw-border-[#364152]">
                    Common options or type your own:
                  </div>
                  <div className="tw-py-1">
                    {['Color', 'Size', 'Material', 'Style', 'Pattern', 'Flavor', 'Weight', 'Length', 'Width', 'Capacity'].map((commonOption) => (
                      <button 
                        key={commonOption}
                        type="button"
                        onClick={() => {
                          onUpdate({ name: commonOption })
                          setShowOptionDropdown(false)
                        }} 
                        className={`tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-sm hover:tw-bg-gray-100 dark:hover:tw-bg-[#233648] tw-text-gray-900 dark:tw-text-white ${option.name === commonOption ? 'tw-bg-primary/10 tw-text-primary' : ''}`}
                      >
                        {commonOption}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <span className="tw-text-[10px] tw-text-gray-500 dark:tw-text-gray-400 tw-mt-1">Option Type</span>
          </div>
        </div>
        <button 
          type="button"
          onClick={() => setShowRemoveConfirm(true)} 
          disabled={disabled}
          className="tw-text-gray-400 hover:tw-text-red-500 tw-transition-colors tw-self-end sm:tw-self-auto"
        >
          <span className="material-symbols-outlined tw-text-[20px]">delete</span>
        </button>
      </div>

      {/* Remove confirmation */}
      {showRemoveConfirm && (
        <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded tw-p-3 tw-mb-3">
          <p className="tw-text-sm tw-text-red-800 tw-mb-2">
            Remove "{option.name}" option? This will also remove {option.values.length} values.
          </p>
          <div className="tw-flex tw-gap-2">
            <button
              type="button"
              onClick={() => {
                onRemove()
                setShowRemoveConfirm(false)
              }}
              className="tw-px-2 tw-py-1 tw-bg-red-600 tw-text-white tw-text-xs tw-rounded hover:tw-bg-red-700"
            >
              Remove
            </button>
            <button
              type="button"
              onClick={() => setShowRemoveConfirm(false)}
              className="tw-px-2 tw-py-1 tw-text-gray-700 tw-text-xs tw-rounded hover:tw-bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Values section matching reference design */}
      <div className="tw-space-y-3">
        <p className="tw-text-xs tw-text-gray-500 tw-mb-2">Add values for this option. Click suggestions or type your own.</p>
        
        {/* Tag-style value display */}
        {option.values.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-3">
            {option.values.map((value) => (
              <div key={value.id} className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-primary/10 tw-text-primary tw-px-3 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-medium tw-border tw-border-primary/20">
                <span>{value.value}</span>
                <button
                  type="button"
                  onClick={() => onRemoveValue(value.id)}
                  disabled={disabled}
                  className="tw-text-primary/70 hover:tw-text-primary tw-transition-colors disabled:tw-opacity-50"
                >
                  <span className="material-symbols-outlined tw-text-[14px]">close</span>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Value input with add button - matching reference design */}
        <div className="tw-relative">
          <input 
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAddValue()
              }
            }}
            className="tw-w-full tw-bg-white dark:tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all" 
            placeholder={`Add ${option.name || 'option'} value...`}
            disabled={disabled}
          />
          <button 
            type="button"
            onClick={handleAddValue} 
            disabled={!newValue.trim() || disabled}
            className="tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-text-primary hover:tw-text-primary/80 tw-transition-colors disabled:tw-opacity-50" 
          >
            <span className="material-symbols-outlined tw-text-[20px]">add</span>
          </button>
        </div>


      </div>
    </div>
  )
}


// Export utilities and types
export {
  generateId,
  generateSKU,
  VariantBulkOperations,
  VariantPreviewTable,
  VariantOptionsBuilder
}

// Variant Preview Table Component - Task 25.5
interface VariantPreviewTableProps {
  variants: VariantCombination[]
  options: VariantOption[]
  onVariantUpdate: (id: string, updates: Partial<VariantCombination>) => void
  onVariantDelete: (id: string) => void
  onBulkOperation: (operation: string, variantIds: string[], value?: any) => void
  loading?: boolean
  disabled?: boolean
  showBulkOperations?: boolean
  // Variant integration service methods
  onLinkVariants?: (merchantProducts: MerchantProduct[], linkingStrategy?: 'auto' | 'manual') => any
  onGenerateSKUs?: (skuOptions: any) => void
  onEstablishHierarchy?: () => any
}

function VariantPreviewTable({
  variants,
  options,
  onVariantUpdate,
  onVariantDelete,
  onBulkOperation,
  loading = false,
  disabled = false,
  showBulkOperations = false,
  onLinkVariants,
  onGenerateSKUs,
  onEstablishHierarchy
}: VariantPreviewTableProps) {
  const dispatch = useDispatch()

  // Modal state for new two-part modal system
  const [showSelectionModal, setShowSelectionModal] = useState(false)
  const [showBrowserModal, setShowBrowserModal] = useState(false)
  const [modalMode, setModalMode] = useState<'link' | 'copy'>('link')
  
  const [showWarehouseStock, setShowWarehouseStock] = useState<string | null>(null)
  const [currentVariantId, setCurrentVariantId] = useState<string | null>(null)
  const [linkTarget, setLinkTarget] = useState<{ type: 'variant'; index: number } | null>(null)

  // Handle image upload for variants
  const handleImageUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>, variantId: string) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string
        onVariantUpdate(variantId, { imageUrl })
      }
      reader.readAsDataURL(file)
    }
  }, [onVariantUpdate])

  // Handle warehouse stock updates
  const handleWarehouseStockUpdate = useCallback((variantId: string, warehouse: string, value: number) => {
    const variant = variants.find(v => v.id === variantId)
    if (!variant) return
    
    const newWarehouseStock = { ...variant.warehouseStock, [warehouse]: value }
    const totalStock = Object.values(newWarehouseStock).reduce((sum, stock) => sum + (stock || 0), 0)
    
    onVariantUpdate(variantId, { 
      warehouseStock: newWarehouseStock,
      stock: totalStock
    })
  }, [variants, onVariantUpdate])

  // Generate combination display name
  const getCombinationName = useCallback((variant: VariantCombination) => {
    return options.map(option => {
      const value = variant.optionValues[option.id]
      return value?.displayName || '-'
    }).join(' / ')
  }, [options])

  // Handle merchant product modal - Go directly to browser modal for linking
  const handleOpenMerchantModal = useCallback((variantId: string, variantIndex: number) => {
    setCurrentVariantId(variantId)
    setLinkTarget({ type: 'variant', index: variantIndex })
    setModalMode('link')
    setShowBrowserModal(true)
  }, [variants])

  // New modal handlers for two-part modal system
  const handleCloseSelectionModal = useCallback(() => {
    setShowSelectionModal(false)
    setCurrentVariantId(null)
    setLinkTarget(null)
  }, [])

  const handleCloseBrowserModal = useCallback(() => {
    setShowBrowserModal(false)
  }, [])

  const handleCreateNewFromSelection = useCallback(() => {
    console.log('Create new merchant product flow for variant:', currentVariantId)
    setShowSelectionModal(false)
    // Implement create new product flow
  }, [currentVariantId])

  const handleOpenBrowserFromSelection = useCallback(() => {
    setShowSelectionModal(false)
    setShowBrowserModal(true)
  }, [])

  const handleSelectMerchantProduct = useCallback((product: MerchantProduct) => {
    if (currentVariantId && linkTarget) {
      // Use variant integration service for proper SKU generation and linking
      const variant = variants.find(v => v.id === currentVariantId)
      if (variant) {
        // Link the merchant product to the variant
        const linkedVariant = {
          ...variant,
          linkedMerchantProduct: product
        }
        
        // Generate SKU using SKU generation service with merchant product data
        const variantCode = Object.values(variant.optionValues)
          .map(v => v.value.slice(0, 3).toUpperCase())
          .join('')
          .slice(0, 6)
        
        const skuResult = skuGenerationService.generateSKU({
          pattern: 'brand-product-variant',
          brand: product.brand,
          product: product.name,
          variant: variantCode
        })
        
        // Update variant with linked product and generated SKU
        onVariantUpdate(currentVariantId, { 
          linkedMerchantProduct: product,
          sku: skuResult.sku
        })
      }
    }
    setShowBrowserModal(false)
    setCurrentVariantId(null)
    setLinkTarget(null)
  }, [currentVariantId, linkTarget, variants, onVariantUpdate])

  const handleCreateNewProduct = useCallback(() => {
    // This would open a create new product flow
    console.log('Create new merchant product flow for variant:', currentVariantId)
    // Keep modal open for create flow
    // The modal will handle the create flow internally
  }, [currentVariantId])

  if (variants.length === 0) {
    return (
      <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-8 tw-text-center">
        <span className="material-symbols-outlined tw-text-4xl tw-text-gray-400 tw-mb-2 tw-block">inventory_2</span>
        <h4 className="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">No Variants Generated</h4>
        <p className="tw-text-sm tw-text-gray-600">
          Add variant options above to automatically generate variant combinations.
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="tw-mb-6">
        <h4 className="tw-text-base tw-font-bold tw-text-gray-900 dark:tw-text-white">Variant Combinations</h4>
        <p className="tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">Set individual pricing and inventory for each variant combination.</p>
      </div>
      
      <div className="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-600">
        <table className="tw-w-full tw-text-left tw-text-sm tw-whitespace-nowrap">
          <thead className="tw-bg-gray-50 dark:tw-bg-gray-800 tw-text-gray-500 dark:tw-text-gray-300">
            <tr>
              <th className="tw-px-4 tw-py-3 tw-font-medium">Image</th>
              <th className="tw-px-4 tw-py-3 tw-font-medium">Variant Combination</th>
              <th className="tw-px-4 tw-py-3 tw-font-medium">Price (Rp)</th>
              <th className="tw-px-4 tw-py-3 tw-font-medium">Stock</th>
              <th className="tw-px-4 tw-py-3 tw-font-medium">SKU</th>
            </tr>
          </thead>
          <tbody className="tw-bg-white dark:tw-bg-gray-900 tw-divide-y tw-divide-gray-200 dark:tw-divide-gray-600">
            {variants.map((variant) => (
              <tr key={variant.id} className="hover:tw-bg-gray-50 dark:hover:tw-bg-gray-800">
                <td className="tw-px-4 tw-py-3">
                  <div className="tw-relative tw-group">
                    {!variant.imageUrl ? (
                      <div className="tw-w-16 tw-h-16 tw-border-2 tw-border-dashed tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-cursor-pointer hover:tw-border-blue-500 hover:tw-bg-blue-50 dark:hover:tw-bg-blue-900/20 tw-transition-all">
                        <input 
                          type="file" 
                          accept="image/*" 
                          className="tw-absolute tw-inset-0 tw-opacity-0 tw-cursor-pointer" 
                          onChange={(e) => handleImageUpload(e, variant.id)}
                          disabled={disabled}
                        />
                        <span className="material-symbols-outlined tw-text-gray-400 tw-text-[20px]">add_photo_alternate</span>
                      </div>
                    ) : (
                      <div className="tw-relative tw-w-16 tw-h-16 tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-600 tw-overflow-hidden">
                        <img src={variant.imageUrl} className="tw-w-full tw-h-full tw-object-cover" alt="Variant" />
                        {!disabled && (
                          <div className="tw-absolute tw-inset-0 tw-bg-black/40 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-flex tw-items-center tw-justify-center">
                            <button 
                              onClick={() => onVariantUpdate(variant.id, { imageUrl: undefined })}
                              className="tw-p-1 tw-bg-red-500/80 hover:tw-bg-red-600 tw-rounded tw-text-white tw-transition-colors" 
                              type="button"
                            >
                              <span className="material-symbols-outlined tw-text-[14px]">close</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                <td className="tw-px-4 tw-py-3">
                  <span className="tw-font-medium tw-text-gray-900 dark:tw-text-white">{getCombinationName(variant)}</span>
                </td>
                <td className="tw-px-4 tw-py-3">
                  <input 
                    className="tw-w-24 tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-sm focus:tw-ring-1 focus:tw-ring-blue-500 tw-text-gray-900 dark:tw-text-white" 
                    type="number" 
                    placeholder="0"
                    value={variant.price || ''}
                    onChange={(e) => onVariantUpdate(variant.id, { price: parseFloat(e.target.value) || 0 })}
                    disabled={disabled}
                  />
                </td>
                <td className="tw-px-4 tw-py-3">
                  <div className="tw-space-y-2">
                    <div className="tw-flex tw-gap-1">
                      <input 
                        className="tw-flex-1 tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-sm focus:tw-ring-1 focus:tw-ring-blue-500 tw-text-gray-900 dark:tw-text-white" 
                        type="number" 
                        placeholder="0"
                        value={variant.stock || ''}
                        onChange={(e) => {
                          const newStock = parseInt(e.target.value) || 0
                          onVariantUpdate(variant.id, { stock: newStock })
                        }}
                        disabled={disabled}
                      />
                      <button 
                        onClick={() => setShowWarehouseStock(showWarehouseStock === variant.id ? null : variant.id)}
                        className="tw-px-3 tw-py-1 tw-bg-gray-100 dark:tw-bg-gray-700 tw-text-gray-700 dark:tw-text-gray-300 tw-rounded-lg hover:tw-bg-gray-200 dark:hover:tw-bg-gray-600 tw-transition-colors"
                        disabled={disabled}
                      >
                        <span className={`material-symbols-outlined tw-text-[18px] ${showWarehouseStock === variant.id ? 'tw-rotate-180' : ''}`}>expand_more</span>
                      </button>
                    </div>
                    
                    {showWarehouseStock === variant.id && variant.warehouseStock && (
                      <div className="tw-space-y-2 tw-pl-3 tw-border-l-2 tw-border-gray-200 dark:tw-border-gray-600">
                        {Object.keys(variant.warehouseStock).length > 0 ? (
                          Object.entries(variant.warehouseStock).map(([warehouseName, stock]) => (
                            <div key={warehouseName} className="tw-flex tw-items-center tw-gap-3">
                              <div className="custom-tooltip tw-w-32 tw-flex-shrink-0">
                                <span className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-truncate tw-block tw-cursor-help">
                                  {warehouseName}:
                                </span>
                                {warehouseName.length > 18 && (
                                  <span className="custom-tooltiptext">{warehouseName}</span>
                                )}
                              </div>
                              <input
                                type="number"
                                placeholder="0"
                                className="tw-flex-1 tw-min-w-16 tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-xs focus:tw-ring-1 focus:tw-ring-blue-500 tw-text-gray-900 dark:tw-text-white"
                                value={stock || ''}
                                onChange={(e) => handleWarehouseStockUpdate(variant.id, warehouseName, parseInt(e.target.value) || 0)}
                                disabled={disabled}
                              />
                            </div>
                          ))
                        ) : (
                          <div className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-italic">
                            No warehouse allocation data available
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                <td className="tw-px-4 tw-py-3">
                  <div className="tw-space-y-2">
                    {/* SKU Input and Link Status */}
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <input 
                        className="tw-flex-1 tw-min-w-20 tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-sm focus:tw-ring-1 focus:tw-ring-blue-500 tw-text-gray-900 dark:tw-text-white" 
                        type="text" 
                        placeholder="SKU"
                        value={variant.sku || ''}
                        onChange={(e) => onVariantUpdate(variant.id, { sku: e.target.value })}
                        disabled={disabled}
                      />
                      
                      {/* Three-state button system */}
                      {!variant.linkedMerchantProduct ? (
                        <button 
                          onClick={() => handleOpenMerchantModal(variant.id, variants.findIndex(v => v.id === variant.id))}
                          disabled={disabled}
                          className="tw-px-2 tw-py-1 tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-text-xs tw-rounded tw-transition-colors tw-flex tw-items-center disabled:tw-opacity-50"
                          title="Link to merchant product"
                          type="button"
                        >
                          <span className="material-symbols-outlined tw-text-[14px]">link</span>
                        </button>
                      ) : (
                        <div className="tw-flex tw-gap-1">
                          <button 
                            onClick={() => handleOpenMerchantModal(variant.id, variants.findIndex(v => v.id === variant.id))}
                            disabled={disabled}
                            className="tw-px-2 tw-py-1 tw-bg-amber-600 hover:tw-bg-amber-700 tw-text-white tw-text-xs tw-rounded tw-transition-colors tw-flex tw-items-center disabled:tw-opacity-50"
                            title="Change linked merchant product"
                            type="button"
                          >
                            <span className="material-symbols-outlined tw-text-[14px]">edit</span>
                          </button>
                          <button 
                            onClick={() => {
                              onVariantUpdate(variant.id, { 
                                linkedMerchantProduct: null,
                                sku: '' // Clear SKU when unlinking
                              })
                            }}
                            disabled={disabled}
                            className="tw-px-2 tw-py-1 tw-bg-gray-500 hover:tw-bg-gray-600 tw-text-white tw-text-xs tw-rounded tw-transition-colors tw-flex tw-items-center disabled:tw-opacity-50"
                            title="Unlink merchant product"
                            type="button"
                          >
                            <span className="material-symbols-outlined tw-text-[14px]">link_off</span>
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Linked Product Information */}
                    {variant.linkedMerchantProduct && (
                      <div className="tw-text-xs tw-text-blue-600 dark:tw-text-blue-400 tw-bg-blue-50 dark:tw-bg-blue-900/20 tw-px-2 tw-py-1 tw-rounded tw-flex tw-items-center tw-gap-1">
                        <span className="material-symbols-outlined tw-text-[12px]">inventory_2</span>
                        <span className="tw-truncate" title={variant.linkedMerchantProduct.name}>
                          {variant.linkedMerchantProduct.name}
                        </span>
                        <span className="tw-text-blue-500 dark:tw-text-blue-300">|</span>
                        <span className="tw-font-mono tw-text-[10px]">
                          {variant.linkedMerchantProduct.sku}
                        </span>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Bulk Operations Panel */}
      {showBulkOperations && variants.length > 0 && (
        <div className="tw-mt-6 tw-bg-gray-50 dark:tw-bg-gray-800 tw-rounded-lg tw-p-4 tw-border tw-border-gray-200 dark:tw-border-gray-600">
          <div className="tw-mb-4">
            <h5 className="tw-text-sm tw-font-semibold tw-text-gray-900 dark:tw-text-white tw-flex tw-items-center tw-gap-2">
              <span className="material-symbols-outlined tw-text-[16px]">tune</span>
              Bulk Operations
            </h5>
            <p className="tw-text-xs tw-text-gray-600 dark:tw-text-gray-400">Apply changes to all variants at once</p>
          </div>
          
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4">
            {/* Bulk Price Operations */}
            <div className="tw-bg-white dark:tw-bg-gray-900 tw-rounded tw-p-3 tw-border tw-border-gray-200 dark:tw-border-gray-600">
              <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                Price Operations
              </label>
              <div className="tw-space-y-2">
                <div className="tw-flex tw-gap-1">
                  <input
                    type="number"
                    placeholder="Amount"
                    className="tw-flex-1 tw-text-xs tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 focus:tw-ring-1 focus:tw-ring-blue-500 tw-text-gray-900 dark:tw-text-white"
                    disabled={disabled}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const value = parseFloat((e.target as HTMLInputElement).value) || 0
                        if (value > 0) {
                          onBulkOperation('updatePrices', variants.map(v => v.id), value)
                          ;(e.target as HTMLInputElement).value = ''
                        }
                      }
                    }}
                  />
                  <button
                    onClick={(e) => {
                      const input = e.currentTarget.previousElementSibling as HTMLInputElement
                      const value = parseFloat(input.value) || 0
                      if (value > 0) {
                        onBulkOperation('updatePrices', variants.map(v => v.id), value)
                        input.value = ''
                      }
                    }}
                    className="tw-px-2 tw-py-1 tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-text-xs tw-rounded tw-transition-colors disabled:tw-opacity-50"
                    disabled={disabled}
                    title="Set all prices"
                  >
                    Set
                  </button>
                </div>
                <div className="tw-flex tw-gap-1">
                  <button
                    onClick={() => onBulkOperation('increasePrices', variants.map(v => v.id), 1000)}
                    className="tw-flex-1 tw-px-2 tw-py-1 tw-bg-green-600 hover:tw-bg-green-700 tw-text-white tw-text-xs tw-rounded tw-transition-colors disabled:tw-opacity-50"
                    disabled={disabled}
                  >
                    +1k
                  </button>
                  <button
                    onClick={() => onBulkOperation('decreasePrices', variants.map(v => v.id), 1000)}
                    className="tw-flex-1 tw-px-2 tw-py-1 tw-bg-orange-600 hover:tw-bg-orange-700 tw-text-white tw-text-xs tw-rounded tw-transition-colors disabled:tw-opacity-50"
                    disabled={disabled}
                  >
                    -1k
                  </button>
                </div>
              </div>
            </div>

            {/* Bulk Stock Operations */}
            <div className="tw-bg-white dark:tw-bg-gray-900 tw-rounded tw-p-3 tw-border tw-border-gray-200 dark:tw-border-gray-600">
              <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                Stock Operations
              </label>
              <div className="tw-space-y-2">
                <div className="tw-flex tw-gap-1">
                  <input
                    type="number"
                    placeholder="Quantity"
                    className="tw-flex-1 tw-text-xs tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 focus:tw-ring-1 focus:tw-ring-blue-500 tw-text-gray-900 dark:tw-text-white"
                    disabled={disabled}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const value = parseInt((e.target as HTMLInputElement).value) || 0
                        if (value >= 0) {
                          onBulkOperation('updateStock', variants.map(v => v.id), value)
                          ;(e.target as HTMLInputElement).value = ''
                        }
                      }
                    }}
                  />
                  <button
                    onClick={(e) => {
                      const input = e.currentTarget.previousElementSibling as HTMLInputElement
                      const value = parseInt(input.value) || 0
                      if (value >= 0) {
                        onBulkOperation('updateStock', variants.map(v => v.id), value)
                        input.value = ''
                      }
                    }}
                    className="tw-px-2 tw-py-1 tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-text-xs tw-rounded tw-transition-colors disabled:tw-opacity-50"
                    disabled={disabled}
                    title="Set all stock"
                  >
                    Set
                  </button>
                </div>
                <div className="tw-flex tw-gap-1">
                  <button
                    onClick={() => onBulkOperation('increaseStock', variants.map(v => v.id), 10)}
                    className="tw-flex-1 tw-px-2 tw-py-1 tw-bg-green-600 hover:tw-bg-green-700 tw-text-white tw-text-xs tw-rounded tw-transition-colors disabled:tw-opacity-50"
                    disabled={disabled}
                  >
                    +10
                  </button>
                  <button
                    onClick={() => onBulkOperation('decreaseStock', variants.map(v => v.id), 10)}
                    className="tw-flex-1 tw-px-2 tw-py-1 tw-bg-orange-600 hover:tw-bg-orange-700 tw-text-white tw-text-xs tw-rounded tw-transition-colors disabled:tw-opacity-50"
                    disabled={disabled}
                  >
                    -10
                  </button>
                </div>
              </div>
            </div>

            {/* Bulk Status & SKU Operations */}
            <div className="tw-bg-white dark:tw-bg-gray-900 tw-rounded tw-p-3 tw-border tw-border-gray-200 dark:tw-border-gray-600">
              <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                Status & SKU Operations
              </label>
              <div className="tw-space-y-2">
                <div className="tw-flex tw-gap-1">
                  <button
                    onClick={() => onBulkOperation('updateStatus', variants.map(v => v.id), VariantStatus.ENABLED)}
                    className="tw-flex-1 tw-px-2 tw-py-1 tw-bg-green-600 hover:tw-bg-green-700 tw-text-white tw-text-xs tw-rounded tw-transition-colors disabled:tw-opacity-50"
                    disabled={disabled}
                  >
                    Enable All
                  </button>
                  <button
                    onClick={() => onBulkOperation('updateStatus', variants.map(v => v.id), VariantStatus.DISABLED)}
                    className="tw-flex-1 tw-px-2 tw-py-1 tw-bg-gray-600 hover:tw-bg-gray-700 tw-text-white tw-text-xs tw-rounded tw-transition-colors disabled:tw-opacity-50"
                    disabled={disabled}
                  >
                    Disable All
                  </button>
                </div>
                <button
                  onClick={() => onBulkOperation('regenerateSKUs', variants.map(v => v.id))}
                  className="tw-w-full tw-px-2 tw-py-1 tw-bg-purple-600 hover:tw-bg-purple-700 tw-text-white tw-text-xs tw-rounded tw-transition-colors disabled:tw-opacity-50 tw-flex tw-items-center tw-justify-center tw-gap-1"
                  disabled={disabled}
                >
                  <span className="material-symbols-outlined tw-text-[12px]">refresh</span>
                  Regenerate SKUs
                </button>
              </div>
            </div>

            {/* Merchant Product Integration */}
            <div className="tw-bg-white dark:tw-bg-gray-900 tw-rounded tw-p-3 tw-border tw-border-gray-200 dark:tw-border-gray-600">
              <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                Merchant Product Linking
              </label>
              <div className="tw-space-y-2">
                <button
                  onClick={() => {
                    // Open modal for bulk merchant product selection
                    console.log('Open bulk merchant product linking modal')
                  }}
                  disabled={disabled}
                  className="tw-w-full tw-px-2 tw-py-1 tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-text-xs tw-rounded tw-transition-colors disabled:tw-opacity-50 tw-flex tw-items-center tw-justify-center tw-gap-1"
                >
                  <span className="material-symbols-outlined tw-text-[12px]">link</span>
                  Link All Variants
                </button>
                <button
                  onClick={() => {
                    // Unlink all merchant products
                    const updatedVariants = variants.map(variant => {
                      if (variant.linkedMerchantProduct) {
                        return {
                          ...variant,
                          linkedMerchantProduct: null,
                          sku: ''
                        }
                      }
                      return variant
                    })
                    onBulkOperation('unlinkAll', variants.map(v => v.id), updatedVariants)
                  }}
                  disabled={disabled || !variants.some(v => v.linkedMerchantProduct)}
                  className="tw-w-full tw-px-2 tw-py-1 tw-bg-red-600 hover:tw-bg-red-700 tw-text-white tw-text-xs tw-rounded tw-transition-colors disabled:tw-opacity-50 tw-flex tw-items-center tw-justify-center tw-gap-1"
                >
                  <span className="material-symbols-outlined tw-text-[12px]">link_off</span>
                  Unlink All
                </button>
                <div className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-pt-1">
                  {variants.filter(v => v.linkedMerchantProduct).length} of {variants.length} linked
                </div>
              </div>
            </div>
          </div>
          
          {/* Bulk Operations Stats */}
          <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-200 dark:tw-border-gray-600">
            <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-text-xs tw-text-center">
              <div>
                <span className="tw-block tw-font-medium tw-text-gray-900 dark:tw-text-white">{variants.length}</span>
                <span className="tw-text-gray-500 dark:tw-text-gray-400">Total Variants</span>
              </div>
              <div>
                <span className="tw-block tw-font-medium tw-text-gray-900 dark:tw-text-white">
                  {variants.filter(v => v.status === VariantStatus.ENABLED).length}
                </span>
                <span className="tw-text-gray-500 dark:tw-text-gray-400">Enabled</span>
              </div>
              <div>
                <span className="tw-block tw-font-medium tw-text-gray-900 dark:tw-text-white">
                  {variants.reduce((sum, v) => sum + v.stock, 0)}
                </span>
                <span className="tw-text-gray-500 dark:tw-text-gray-400">Total Stock</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {loading && (
        <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
          <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
        </div>
      )}
      
      {/* New Two-Part Modal System */}
      <MerchantProductSelectionModal
        isOpen={showSelectionModal}
        onClose={handleCloseSelectionModal}
        onCreateNew={handleCreateNewFromSelection}
        onOpenBrowser={handleOpenBrowserFromSelection}
        mode={modalMode}
      />
      
      <MerchantProductBrowserModal
        isOpen={showBrowserModal}
        onClose={handleCloseBrowserModal}
        onSelectProduct={handleSelectMerchantProduct}
        onCopyProduct={() => {}}
        mode={modalMode}
        title="Link Variant to Merchant Product"
        showBackButton={true}
        onBack={handleCloseSelectionModal}
        linkTarget={linkTarget}
      />
    </div>
  )
}

export default VariantOptionsBuilder